/**
 * The player rig: a group that locomotion moves, containing the camera (headset) and the
 * controller / hand spaces. In XR the camera pose comes from the headset relative to the rig;
 * on desktop the camera sits at standing eye height and mouse look rotates it.
 */
import * as THREE from 'three';

export const STANDING_EYE_HEIGHT = 1.65;

export class PlayerRig {
  readonly group = new THREE.Group();
  readonly camera: THREE.PerspectiveCamera;
  /** Extra vertical offset applied after seated-height recalibration. */
  heightOffset = 0;

  constructor(aspect: number) {
    this.group.name = 'player-rig';
    this.camera = new THREE.PerspectiveCamera(70, aspect, 0.05, 400);
    this.camera.position.set(0, STANDING_EYE_HEIGHT, 0);
    this.group.add(this.camera);
  }

  /** World-space head position. */
  headPosition(target = new THREE.Vector3()): THREE.Vector3 {
    return this.camera.getWorldPosition(target);
  }

  /** World-space yaw of the head in radians. */
  headYaw(): number {
    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    return Math.atan2(-dir.x, -dir.z);
  }

  /** Horizontal forward vector of the head, projected on the floor. */
  headForward(target = new THREE.Vector3()): THREE.Vector3 {
    this.camera.getWorldDirection(target);
    target.y = 0;
    return target.lengthSq() > 0 ? target.normalize() : target.set(0, 0, -1);
  }

  /** Floor position under the head. */
  feetPosition(target = new THREE.Vector3()): THREE.Vector3 {
    this.headPosition(target);
    target.y = this.group.position.y;
    return target;
  }

  /** Places the feet at a world position, keeping the head's offset within the play space. */
  teleportTo(worldFeet: THREE.Vector3): void {
    const head = this.headPosition(new THREE.Vector3());
    const dx = head.x - this.group.position.x;
    const dz = head.z - this.group.position.z;
    this.group.position.set(worldFeet.x - dx, worldFeet.y, worldFeet.z - dz);
  }

  /** Rotates the rig around the head's vertical axis. */
  rotateAroundHead(radians: number): void {
    const head = this.headPosition(new THREE.Vector3());
    const offset = new THREE.Vector3().subVectors(this.group.position, head);
    offset.applyAxisAngle(new THREE.Vector3(0, 1, 0), radians);
    this.group.position.copy(head).add(offset);
    this.group.rotation.y += radians;
  }

  /** Face a world point (yaw only), used on spawn. */
  faceTowards(target: THREE.Vector3, inXr: boolean): void {
    const feet = this.feetPosition(new THREE.Vector3());
    const yaw = Math.atan2(-(target.x - feet.x), -(target.z - feet.z));
    if (inXr) {
      const current = this.headYaw();
      this.rotateAroundHead(yaw - current);
    } else {
      this.group.rotation.y = yaw;
      this.camera.rotation.set(0, 0, 0);
    }
  }

  /**
   * Seated-height recalibration: lifts the rig so the current head height matches standing
   * eye height. Pass the head height in rig-local space (local-floor reference).
   */
  recalibrateHeight(): number {
    const localHead = this.camera.position.y;
    const offset = STANDING_EYE_HEIGHT - localHead;
    this.heightOffset = Math.abs(offset) < 0.08 ? 0 : offset;
    this.group.position.y = this.heightOffset;
    return this.heightOffset;
  }

  resetHeight(): void {
    this.heightOffset = 0;
    this.group.position.y = 0;
  }
}
